import { NONE } from '@mona-roadmap/domain';
import type { ColumnDef } from './columns';

/** Pure model behind the keyboard-first filter editor (kept free of React so it is unit-tested). */

/** Filterable columns whose label matches what the user typed; prefix matches first, then substring matches. */
export function matchColumns(columns: readonly ColumnDef[], query: string): ColumnDef[] {
  const q = query.trim().toLowerCase();
  const filterable = columns.filter((c) => c.filter);
  if (!q) return filterable;
  const words = (c: ColumnDef) => `${c.label} ${c.hint ?? ''}`.toLowerCase();
  const starts = filterable.filter((c) => c.label.toLowerCase().startsWith(q));
  const wordStarts = filterable.filter((c) => !starts.includes(c) && words(c).split(/\s+/).some((w) => w.startsWith(q)));
  const contains = filterable.filter((c) => !starts.includes(c) && !wordStarts.includes(c) && words(c).includes(q));
  return [...starts, ...wordStarts, ...contains];
}

/** The set of checked values shown in the editor. No filter means everything is checked. */
export function effectiveSelection(stored: readonly string[], allValues: readonly string[]): Set<string> {
  if (stored.length === 0) return new Set(allValues);
  if (stored.length === 1 && stored[0] === NONE) return new Set();
  return new Set(stored);
}

/** Converts a checked set back into the stored filter value: everything -> no filter, nothing -> NONE. */
export function commitSelection(checked: ReadonlySet<string>, allValues: readonly string[]): string[] {
  const kept = allValues.filter((v) => checked.has(v));
  if (kept.length >= allValues.length) return [];
  if (kept.length === 0) return [NONE];
  return kept;
}

export function toggleValue(stored: readonly string[], allValues: readonly string[], value: string): string[] {
  const next = effectiveSelection(stored, allValues);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return commitSelection(next, allValues);
}

/** Enter after typing: keep only the values that match the typed text. */
export function keepOnlyMatching(visibleValues: readonly string[], allValues: readonly string[]): string[] {
  return commitSelection(new Set(visibleValues), allValues);
}

export function filterByText<T extends { label: string }>(items: readonly T[], query: string): T[] {
  const q = query.trim().toLowerCase();
  return q ? items.filter((i) => i.label.toLowerCase().includes(q)) : [...items];
}

/** Letter shortcuts use the physical key so they work on any keyboard layout (e.g. Hebrew). */
export type ShortcutAction = 'expandAll' | 'collapseAll' | 'filter' | 'theme' | 'search' | 'refresh';

export function shortcutFor(e: Pick<KeyboardEvent, 'code' | 'ctrlKey' | 'metaKey' | 'altKey' | 'shiftKey'>): ShortcutAction | null {
  if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return null;
  switch (e.code) {
    case 'KeyE':
      return 'expandAll';
    case 'KeyC':
      return 'collapseAll';
    case 'KeyF':
      return 'filter';
    case 'KeyT':
      return 'theme';
    case 'KeyR':
      return 'refresh';
    case 'Slash':
      return 'search';
    default:
      return null;
  }
}
